import { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Menu,
  Chip,
  Badge,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useListAgentTools } from "services/agents";
import { ListAgentToolsRequest } from "protogen/agents_service_pb";

type ToolPreference = "always" | "sometimes" | "never";

type ToolsConfiguration = {
  always?: string[];
  never?: string[];
};

type AgentToolsSelectorProps = {
  value: ToolsConfiguration;
  onChange: (value: ToolsConfiguration) => void;
  onClose?: () => void;
  disabled?: boolean;
};

const COLORS = {
  always: {
    color: "#468E3E",
    background: "#EAF2EA",
    border: "#468E3E",
  },
  sometimes: {
    color: "#9A6518",
    background: "#FBF2E5",
    border: "#9A6518",
  },
  never: {
    color: "#C14743",
    background: "#FCDBCD",
    border: "#C14743",
  },
};

export default ({
  value,
  onChange,
  onClose,
  disabled,
}: AgentToolsSelectorProps) => {
  const theme = useTheme();
  const chipRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tools, setTools] = useState<
    { name: string; displayName: string; description: string }[]
  >([]);
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(
    new Set(),
  );
  const { request: listAgentTools } = useListAgentTools();
  const isOpen = Boolean(anchorEl);

  useEffect(() => {
    const loadTools = async () => {
      const response = await listAgentTools(new ListAgentToolsRequest());
      if (response?.tools) {
        setTools(
          response.tools.map((t) => ({
            name: t.name,
            displayName: t.displayName,
            description: t.description,
          })),
        );
      }
    };
    loadTools();
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(chipRef.current);
  };

  const toggleDescription = (toolName: string) => {
    setExpandedDescriptions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(toolName)) {
        newSet.delete(toolName);
      } else {
        newSet.add(toolName);
      }
      return newSet;
    });
  };

  const handleClose = () => {
    setAnchorEl(null);
    onClose?.();
  };

  const getToolPreference = (toolName: string): ToolPreference => {
    if (value.always?.includes(toolName)) return "always";
    if (value.never?.includes(toolName)) return "never";
    return "sometimes";
  };

  const handleToolPreferenceChange = (
    toolName: string,
    newPreference: ToolPreference,
  ) => {
    const newAlways = (value.always || []).filter((t) => t !== toolName);
    const newNever = (value.never || []).filter((t) => t !== toolName);

    if (newPreference === "always") {
      newAlways.push(toolName);
    } else if (newPreference === "never") {
      newNever.push(toolName);
    }

    onChange({
      always: newAlways.length > 0 ? newAlways : undefined,
      never: newNever.length > 0 ? newNever : undefined,
    });
  };

  const modifiedCount =
    (value.always?.length || 0) + (value.never?.length || 0);

  const ChevronIcon = isOpen ? ChevronUp : ChevronDown;

  return (
    <div ref={chipRef}>
      <Badge
        badgeContent={modifiedCount}
        color="info"
        sx={{
          "& .MuiBadge-badge": {
            right: 3,
            top: 3,
            fontSize: "10px",
            height: "16px",
            minWidth: "16px",
          },
        }}
        invisible={modifiedCount === 0}
      >
        <Chip
          label="Tools"
          onClick={handleClick}
          color="default"
          variant="outlined"
          disabled={disabled}
          deleteIcon={
            <ChevronIcon
              size={20}
              strokeWidth="2px"
              color={theme.palette.text.tertiary}
            />
          }
          onDelete={handleClick}
          sx={{
            cursor: "pointer",
            height: "40px",
            border: `1px solid ${theme.palette.border}`,
            borderRadius: "100px !important",
            paddingLeft: "12px",
            "& .MuiChip-label": {
              paddingRight: "8px",
            },
            "& .MuiChip-deleteIcon": {
              margin: "0 8px 0 0",
              "&:hover": {
                color: theme.palette.text.tertiary,
              },
            },
          }}
        />
      </Badge>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: "400px",
              maxWidth: "500px",
              maxHeight: "400px",
              overflowY: "auto",
              padding: "16px",
            },
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {tools
            .sort((a, b) => a.displayName.localeCompare(b.displayName))
            .map((tool) => {
              const currentPreference = getToolPreference(tool.name);
              const isExpanded = expandedDescriptions.has(tool.name);
              return (
                <Box
                  key={tool.name}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: "16px",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body" sx={{ display: "block" }}>
                      {tool.displayName}
                    </Typography>
                    {tool.description && (
                      <Typography
                        variant="bodySmall"
                        onClick={() => toggleDescription(tool.name)}
                        sx={{
                          color: "#667085",
                          display: "block",
                          overflow: isExpanded ? "visible" : "hidden",
                          textOverflow: isExpanded ? "clip" : "ellipsis",
                          whiteSpace: isExpanded ? "normal" : "nowrap",
                          cursor: "pointer",
                        }}
                      >
                        {tool.description}
                      </Typography>
                    )}
                  </Box>
                  <ToggleButtonGroup
                    value={currentPreference}
                    exclusive
                    onChange={(_, newValue) => {
                      if (newValue !== null) {
                        handleToolPreferenceChange(tool.name, newValue);
                      }
                    }}
                    size="small"
                    disabled={disabled}
                    sx={{
                      flexShrink: 0,
                      "& .MuiToggleButton-root": {
                        padding: "4px 12px",
                        fontSize: "12px",
                        textTransform: "none",
                        border: "1px solid #D4D4D4",
                        "&:not(:first-of-type)": {
                          marginLeft: "-1px",
                        },
                        "&.Mui-selected, &.Mui-selected:hover": {
                          zIndex: 1,
                        },
                      },
                      "& .MuiToggleButton-root[value='always']": {
                        "&.Mui-selected, &.Mui-selected:hover": {
                          color: COLORS.always.color,
                          backgroundColor: COLORS.always.background,
                          borderColor: COLORS.always.border,
                        },
                      },
                      "& .MuiToggleButton-root[value='sometimes']": {
                        "&.Mui-selected, &.Mui-selected:hover": {
                          color: COLORS.sometimes.color,
                          backgroundColor: COLORS.sometimes.background,
                          borderColor: COLORS.sometimes.border,
                        },
                      },
                      "& .MuiToggleButton-root[value='never']": {
                        "&.Mui-selected, &.Mui-selected:hover": {
                          color: COLORS.never.color,
                          backgroundColor: COLORS.never.background,
                          borderColor: COLORS.never.border,
                        },
                      },
                    }}
                  >
                    <ToggleButton value="always">Always</ToggleButton>
                    <ToggleButton value="sometimes">Sometimes</ToggleButton>
                    <ToggleButton value="never">Never</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              );
            })}
        </Box>
      </Menu>
    </div>
  );
};
